import PolicyPage from "@/components/shared/PolicyPage";

const SECTIONS = [
  {
    p: [
      'This Cookie Policy explains how Carztohire ("Carztohire," "we," "our," or "us") uses cookies and similar tracking technologies when you visit our website or use our online services. It describes the types of cookies we use, the information they collect, and the choices available to you regarding their use. This Cookie Policy should be read together with our Privacy Policy, which explains how we collect, use, disclose, and protect your personal information.',
      "By continuing to browse or use our website, you agree to our use of cookies as described in this Cookie Policy, unless you have disabled them through your browser or device settings where permitted by law.",
    ],
  },
  {
    h: "What are cookies?",
    p: [
      "Cookies are small text files that websites place on your computer, smartphone, tablet, or other internet-enabled device when you visit a website. These files allow the website to recognize your browser, remember your preferences, improve functionality, and provide a more personalized browsing experience.",
      "Cookies generally do not contain information that directly identifies you. However, in certain situations, the information collected through cookies may be associated with other data that can identify you. When this occurs, we treat the information in accordance with our Privacy Policy.",
      "Cookies cannot install software, access files stored on your device, or transmit viruses or malicious code.",
    ],
  },
  {
    h: "Why we use cookies",
    p: "Carztohire uses cookies and similar technologies for several business and operational purposes, including to:",
    list: [
      "Ensure our website functions properly.",
      "Maintain the security of our online services.",
      "Process vehicle rental reservations.",
      "Remember your preferences and settings.",
      "Improve website speed and performance.",
      "Personalize your browsing experience.",
      "Analyze visitor behavior and website traffic.",
      "Measure the effectiveness of advertising campaigns.",
      "Deliver content and promotions that may be relevant to your interests.",
      "Identify and resolve technical issues.",
      "Prevent fraudulent or unauthorized activities.",
    ],
  },
  {
    h: "Who this cookie policy applies to",
    p: "This Cookie Policy applies to all individuals who visit or interact with the Carztohire website, regardless of the device, browser, or location from which the website is accessed.",
  },
  {
    h: "Types of cookies we use",
    p: "We use different categories of cookies depending on their purpose and duration.",
  },
  {
    sh: "1. Essential cookies",
    p: "Essential cookies are necessary for the operation of our website. Without these cookies, certain features and services cannot function correctly. These cookies help us:",
    list: [
      "Maintain secure user sessions.",
      "Process reservations.",
      "Enable website navigation.",
      "Protect against fraudulent activity.",
      "Verify user requests.",
      "Balance website traffic across servers.",
    ],
  },
  { p: "Because these cookies are essential to providing our Services, they generally cannot be disabled through our website." },
  {
    sh: "2. Performance and analytics cookies",
    p: "Performance cookies collect information about how visitors use our website. The information is aggregated and helps us understand how users interact with our Services. These cookies may collect information such as:",
    list: [
      "Pages visited",
      "Time spent on individual pages",
      "Website navigation paths",
      "Error messages",
      "Device type",
      "Browser information",
      "Website performance metrics",
    ],
  },
  { p: "This information allows us to improve website functionality, identify technical issues, and enhance the overall user experience." },
  {
    sh: "3. Functional cookies",
    p: "Functional cookies remember choices you make while using our website to provide a more personalized experience. Examples include remembering:",
    list: [
      "Preferred language",
      "Geographic region",
      "Previously entered information",
      "User preferences",
      "Accessibility settings",
    ],
  },
  { p: "These cookies improve convenience by reducing the need to repeatedly enter the same information." },
  {
    sh: "4. Advertising and marketing cookies",
    p: "Advertising cookies help us and our marketing partners provide advertisements that are more relevant to your interests. These cookies may be used to:",
    list: [
      "Display personalized advertisements.",
      "Measure advertising effectiveness.",
      "Limit repetitive advertisements.",
      "Build audience segments.",
      "Understand visitor interests.",
      "Track responses to marketing campaigns.",
    ],
  },
  { p: "Advertising cookies may be placed by Carztohire or trusted third-party advertising providers." },
  {
    h: "First-party and third-party cookies",
    sh: "First-party cookies",
    p: "These cookies are created directly by Carztohire and are used to support the operation and functionality of our website.",
  },
  {
    sh: "Third-party cookies",
    p: "Some cookies are placed by trusted third-party service providers that assist us with website analytics, advertising, payment processing, customer support, and other business operations. These providers maintain their own privacy practices, and we encourage you to review their respective privacy policies for additional information.",
  },
  {
    h: "Session cookies and persistent cookies",
    sh: "Session cookies",
    p: "Session cookies are temporary files that remain active only while your browser is open. They are automatically removed once your browsing session ends. These cookies help maintain website functionality during your visit.",
  },
  {
    sh: "Persistent cookies",
    p: "Persistent cookies remain on your device after you close your browser until they expire automatically or are manually deleted. These cookies help us remember your preferences when you return to our website.",
  },
  {
    h: "Information collected through cookies",
    p: "Depending on the type of cookie used, we may collect information including:",
    list: [
      "Internet Protocol (IP) address",
      "Browser type and version",
      "Operating system",
      "Device information",
      "Screen resolution",
      "Language preferences",
      "Website pages visited",
      "Date and time of visits",
      "Referring websites",
      "Exit pages",
      "Click activity",
      "Session duration",
      "Search activity",
      "Approximate geographic location",
      "Advertisement interactions",
    ],
  },
  { p: "Most cookie data is collected automatically during your interaction with our website." },
  {
    h: "How we use cookie information",
    p: "Information collected through cookies may be used to:",
    list: [
      "Improve website performance.",
      "Personalize website content.",
      "Remember user preferences.",
      "Analyze website traffic.",
      "Monitor website security.",
      "Detect suspicious activity.",
      "Improve reservation functionality.",
      "Evaluate marketing campaigns.",
      "Better understand customer interests.",
      "Develop and improve our online services.",
    ],
  },
  { p: "Where practical, we may use aggregated or anonymized data that does not directly identify individual users." },
  {
    h: "Analytics services",
    p: "Carztohire uses analytics tools to better understand how visitors interact with our website. These services help us evaluate:",
    list: [
      "Visitor traffic",
      "User engagement",
      "Popular website content",
      "Navigation behavior",
      "Device usage",
      "Marketing performance",
      "Technical issues",
    ],
  },
  { p: "Analytics information is generally used in aggregate form to improve the quality, usability, and effectiveness of our website." },
  {
    h: "Third-party advertising",
    p: "We may work with advertising networks and marketing partners that use cookies or similar technologies to provide interest-based advertising. These technologies may help:",
    list: [
      "Deliver advertisements relevant to your interests.",
      "Measure campaign performance.",
      "Limit duplicate advertisements.",
      "Understand visitor behavior across websites.",
      "Improve future advertising campaigns.",
    ],
  },
  { p: "Some advertising partners may collect information across multiple websites in accordance with their own privacy policies." },
  {
    h: "Managing your cookie preferences",
    p: "You have several options for controlling how cookies are used. Most web browsers allow you to:",
    list: [
      "Accept or reject cookies.",
      "Delete stored cookies.",
      "Block cookies from specific websites.",
      "Receive notifications before cookies are stored.",
      "Disable third-party cookies.",
    ],
  },
  {
    p: "The method for managing cookies varies depending on your browser. Please refer to your browser's help documentation for specific instructions. If you use multiple browsers or devices, you may need to update your cookie preferences separately on each one.",
  },
  {
    h: "Impact of disabling cookies",
    p: "Choosing to disable cookies may affect certain features of the Carztohire website. Depending on which cookies are disabled, you may experience:",
    list: [
      "Slower website performance.",
      "Repeated login requests.",
      "Loss of saved preferences.",
      "Limited website functionality.",
      "Reduced personalization.",
      "Difficulty completing online reservations.",
      "Certain pages or features not functioning correctly.",
    ],
  },
  { p: "Essential cookies remain necessary for the proper operation and security of our website." },
  {
    h: "Changes to this cookie policy",
    p: "Carztohire may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or our business practices. When changes are made, the updated version will be posted on this page with a revised Effective Date. We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies and similar technologies.",
  },
];

export default function CookiePolicy() {
  return (
    <PolicyPage
      overline="Legal · Cookie Policy"
      title="Cookie Policy."
      intro="How we use cookies and similar tracking technologies to make Carztohire work — and the choices you have."
      effectiveDate="01 July 2026"
      sections={SECTIONS}
      testId="cookie-policy"
    />
  );
}
